import {
    Box,
    Button,
    Flex,
    FormControl,
    FormLabel,
    Input,
    Text,
    Textarea,
    VStack,
    Checkbox,
    FormErrorMessage
} from "@chakra-ui/react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useEffect, useMemo, useRef, useCallback } from "react";
import {
    Cookie,
    AutoSave
} from '../../../events/pages/AllEventsPage/cookie-utils';
import InlineErrorInput from "../../../../shared/components/InlineErrorInput/InlineErrorInput";

const UserDataSchema = Yup.object().shape({
    firstName: Yup.string().required("Imię jest wymagane"),
    lastName: Yup.string().required("Nazwisko jest wymagane"),
    email: Yup.string().email("Nieprawidłowy e-mail").required("E-mail jest wymagany"),
    phone: Yup.string(),
    info: Yup.string(),
    agreement1: Yup.boolean().oneOf([true], "Zgoda jest wymagana"),
    agreement2: Yup.boolean()
});

const UserDataStep = ({ data, update, next, t }) => {
    const eventId = data?.eventId ?? "global";
    const COOKIE_KEY = `checkout:${eventId}:user`;

    // 1) inicjalizacja z cookie (jeśli jest), w przeciwnym razie z props `data`
    const cookieVals = Cookie.get(COOKIE_KEY);
    const initialValues = useMemo(() => ({
        firstName: cookieVals?.firstName ?? data.firstName ?? "",
        lastName: cookieVals?.lastName ?? data.lastName ?? "",
        email: cookieVals?.email ?? data.email ?? "",
        phone: cookieVals?.phone ?? data.phone ?? "",
        info: cookieVals?.info ?? data.info ?? "",
        agreement1: cookieVals?.agreement1 ?? data.agreement1 ?? false,
        agreement2: cookieVals?.agreement2 ?? data.agreement2 ?? false
    }), [cookieVals, data]);

    // 2) jeśli mieliśmy cookie, zsynchronizuj w górę (żeby parent też je miał)
    useEffect(() => {
        if (cookieVals) update(initialValues);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleAutoSave = useCallback((vals) => {
        Cookie.set(COOKIE_KEY, vals, 7);
        update(vals);
    }, [COOKIE_KEY, update]);

    return (
        <Formik
            initialValues={initialValues}
            enableReinitialize={true}
            validationSchema={UserDataSchema}
            onSubmit={(values) => {
                Cookie.set(COOKIE_KEY, values, 7);
                update(values);
                next();
            }}
        >
            {({ isSubmitting, values }) => (
                <Form>
                    <AutoSave values={values} onSave={handleAutoSave} />
                    <VStack spacing={4} align="stretch">
                        <Text fontFamily="Nunito" fontSize="18px" fontWeight="500">
                            {t('order.step.user_data.title')}
                        </Text>
                        <Flex gap={4} alignItems={"flex-end"}>
                            <Box flex="1" alignSelf={"start"}>
                                <InlineErrorInput
                                    name="firstName"
                                    label={t('order.form.name')}
                                    isRequired
                                />
                            </Box>
                            <Box flex="1" alignSelf={"start"}>
                                <InlineErrorInput
                                    name="lastName"
                                    label={t('order.form.lastName')}
                                    isRequired
                                />
                            </Box>
                        </Flex>
                        <Flex gap={4} alignItems={"flex-end"}>
                            <Box flex="1" alignSelf={"start"}>
                                <InlineErrorInput
                                    name="email"
                                    type="email"
                                    label={t('order.form.email')}
                                    isRequired
                                    autoComplete="email"
                                />
                            </Box>
                            <Box flex="1" alignSelf={"start"}>
                                <InlineErrorInput
                                    name="phone"
                                    type="tel"
                                    label={t('order.form.phone')}
                                    autoComplete="tel"
                                />
                            </Box>
                        </Flex>

                        <InlineErrorInput
                            name="info"
                            label={t('order.form.information')}
                            as={Textarea}
                            rows={4}
                            h={'4rem'}
                        />

                        <Flex flexDirection={"column"} gap="8px">
                            <Text fontWeight="600" fontSize="16px">
                                {t('order.form.consent.messaage')}
                            </Text>
                            <Field name="agreement1">
                                {({ field, form }) => (
                                    <FormControl isInvalid={form.errors.agreement1 && form.touched.agreement1}>
                                        <Checkbox {...field} isChecked={field.value}>
                                            {t('order.form.consent.agreement1.messaage')}
                                        </Checkbox>
                                        <FormErrorMessage>{form.errors.agreement1}</FormErrorMessage>
                                    </FormControl>
                                )}
                            </Field>

                            <Field name="agreement2">
                                {({ field }) => (
                                    <FormControl>
                                        <Checkbox {...field} isChecked={field.value}>
                                            {t('order.form.consent.agreement2.messaage')}
                                        </Checkbox>
                                    </FormControl>
                                )}
                            </Field>
                        </Flex>

                        <Button
                            w={"full"}
                            type="submit"
                            bgColor={'#089A7A'}
                            color={'#FFFFFF'}
                            borderRadius="4px"
                            fontFamily="Nunito"
                            fontWeight="400"
                            fontSize="18px"
                            isLoading={isSubmitting}
                            alignSelf="flex-end"
                            py="30px"
                            gap="12px"
                        >
                            {t('order.form.move_on')}
                            <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M14 5L21.5 12.5M21.5 12.5L14 20M21.5 12.5H3.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </Button>
                    </VStack>
                </Form>
            )}
        </Formik>
    );
}

export default UserDataStep;
